import { useEffect, useMemo, useRef, useState } from "react";
import { getProspect, updateProspect, deleteProspect } from "../../interfaces/prospect";
import { X, Mail, MessageSquare, Save, Copy, ExternalLink, Trash2, Building2 } from "lucide-react";
import { FaLinkedinIn } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getEmailTemplates } from "../../interfaces/emailTemplate";
import { Select } from "../ui/Select";
import { LinkedinStageMenu } from "../linkedin/LinkedinStageMenu";
import { getCrmStages } from "../../interfaces/linkedinProspecting";
import { LinkedinStage } from "../../lib/linkedinCrm";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    prospectId: string | null;
    onSave: (updated: any) => void;
    onDelete?: (id: string) => void;
    allTags?: string[];
    onTagCreate?: (tag: string) => void;
    /** Told when the prospect's LinkedIn stage is changed from here, so the board can follow. */
    onLinkedinStage?: (id: string, stage: LinkedinStage, followUpAt: string | null) => void;
};

const cx = (...classes: (string | false | undefined)[]) =>
    classes.filter(Boolean).join(" ");

const baseInput =
    "w-full border border-border rounded-lg bg-surface text-sm text-ink placeholder-ink-faint px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-shadow";

const Input = (props: any) => (
    <input {...props} className={cx(baseInput, props.className)} />
);
const Textarea = (props: any) => (
    <textarea
        {...props}
        className={cx(
            baseInput,
            "w-full resize-y min-h-[96px]",
            props.className,
        )}
    />
);

const avatarColor = (name: string) => {
    const colors = [
        "bg-[#BFDBFE] text-[#1E3A8A]",
        "bg-[#C7F9E9] text-[#065F46]",
        "bg-[#E9D5FF] text-[#5B21B6]",
        "bg-[#FDE68A] text-[#92400E]",
        "bg-[#FECACA] text-[#991B1B]",
        "bg-[#C7D2FE] text-[#3730A3]",
        "bg-[#D1FAE5] text-[#065F46]",
        "bg-[#FFEDD5] text-[#9A3412]",
    ];
    const idx =
        (name || "")
            .split("")
            .reduce((a: number, c: string) => a + c.charCodeAt(0), 0) %
        colors.length;
    return colors[idx];
};

const timeUntil = (iso?: string) => {
    if (!iso) return "";
    const now = Date.now();
    const then = new Date(iso).getTime();
    const diffMs = then - now;
    const abs = Math.abs(diffMs);
    const mins = Math.round(abs / (60 * 1000));
    if (mins === 0) return "à l'instant";
    if (mins < 60) return diffMs >= 0 ? `dans ${mins}m` : `il y a ${mins}m`;
    const hours = Math.round(abs / (60 * 60 * 1000));
    if (hours < 48) return diffMs >= 0 ? `dans ${hours}h` : `il y a ${hours}h`;
    const days = Math.round(abs / (24 * 60 * 60 * 1000));
    return diffMs >= 0 ? `dans ${days}j` : `il y a ${days}j`;
};

// URL helpers for clickable fields
const toUrl = (s?: string) => {
    if (!s) return "";
    const v = String(s).trim();
    if (!v) return "";
    return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};
// prettyUrl removed (no longer used)

export default function EditProspectModal({
    isOpen,
    onClose,
    prospectId,
    onSave,
    onDelete,
    allTags,
    onTagCreate,
    onLinkedinStage,
}: Props) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_loading, setLoading] = useState(false);
    const [form, setForm] = useState<any>({});
    const [_original, setOriginal] = useState<any>(null);
    /** Its stage on the LinkedIn page; "discover" until it is invited there. */
    const [linkedinStage, setLinkedinStage] = useState<LinkedinStage>("discover");
    
    // Tab state
    const [activeTab, setActiveTab] = useState<"details" | "reminder" | "notes" | "logs">("details");
    
    // Message state
    const [newMessage, setNewMessage] = useState<string>("");
    
    // Log type selector
    const [selectedLogType, setSelectedLogType] = useState<"message" | "send-linkedin" | "received-linkedin" | "send-email" | "received-email">("message");
    
    // LinkedIn composer popup state
    const [showLinkedInComposer, setShowLinkedInComposer] = useState(false);
    const [linkedInTemplates, setLinkedInTemplates] = useState<any[]>([]);
    const [linkedInMessage, setLinkedInMessage] = useState("");
    const [searchTemplate, setSearchTemplate] = useState("");
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const messageRef = useRef<HTMLTextAreaElement>(null);
    const logsRef = useRef<HTMLDivElement>(null);
    
    // Reminder state
    const [showReminderForm, setShowReminderForm] = useState(false);
    const [reminderAction, setReminderAction] = useState("");
    const in3DaysToISOString = () => {
        const date = new Date();
        date.setDate(date.getDate() + 3);
        return date.toISOString().slice(0,10);
    };
    const [reminderDate, setReminderDate] = useState(in3DaysToISOString());
    const [reminderTime, setReminderTime] = useState("14:00");
    
    // Tag input state
    const [tagInput, setTagInput] = useState("");
    const [showTagSuggestions, setShowTagSuggestions] = useState(false);
    
    // Social networks state
    const [showAddSocial, setShowAddSocial] = useState(false);
    const [newSocialPlatform, setNewSocialPlatform] = useState("");
    const [newSocialUrl, setNewSocialUrl] = useState("");
    
    // Delete confirmation state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    
    const navigate = useNavigate();

    // Add a log entry
    const addLog = async (type: string, message: string) => {
        if (!prospectId || !message.trim()) return;
        
        const log = {
            type,
            message: message.trim(),
            date: new Date().toISOString(),
        };
        
        const prevLogs = Array.isArray(form?.logs) ? [...form.logs] : [];
        const nextLogs = [...prevLogs, log];
        
        try {
            await updateProspect(prospectId, { logs: nextLogs } as any);
            set("logs", nextLogs);
            setNewMessage("");
        } catch (e) {
            console.error("Failed to add log:", e);
        }
    };
    
    // Handle sending message
    const handleSendMessage = () => {
        if (!newMessage.trim()) return;
        addLog(selectedLogType, newMessage);
    };
    
    // Open LinkedIn profile
    const openLinkedInProfile = () => {
        const url = form?.linkedinUrl;
        if (url) {
            const fullUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
            window.open(fullUrl, "_blank");
        }
    };
    
    // Load templates when composer opens
    useEffect(() => {
        if (showLinkedInComposer && linkedInTemplates.length === 0) {
            setLoadingTemplates(true);
            getEmailTemplates()
                .then((templates: any[]) => {
                    // Filter for LinkedIn/text templates
                    const filtered = templates.filter(t => 
                        t.type !== 'sms' && (t.text || t.html)
                    );
                    setLinkedInTemplates(filtered);
                })
                .catch(err => console.error("Failed to load templates:", err))
                .finally(() => setLoadingTemplates(false));
        }
    }, [showLinkedInComposer, linkedInTemplates.length]);
    
    // Set default message when log type changes
    useEffect(() => {
        const firstName = form.firstName || "";
        const lastName = form.lastName || "";
        const fullName = `${firstName} ${lastName}`.trim() || form.name || "le prospect";
        
        switch (selectedLogType) {
            case "send-linkedin":
                setNewMessage(`Message LinkedIn envoyé à ${fullName}`);
                break;
            case "received-linkedin":
                setNewMessage(`Message LinkedIn reçu de ${fullName}`);
                break;
            case "send-email":
                setNewMessage(`Email envoyé à ${fullName}`);
                break;
            case "received-email":
                setNewMessage(`Email reçu de ${fullName}`);
                break;
            case "message":
                setNewMessage("");
                break;
            default:
                setNewMessage("");
        }
    }, [selectedLogType, form.firstName, form.lastName, form.name]);
    
    // Close tag suggestions when clicking outside (disabled since we use onBlur now)
    // The onBlur handler in the input takes care of closing
    
    // Apply template with personalization
    const applyTemplate = (template: string) => {
        const firstName = form.firstName || "";
        const lastName = form.lastName || "";
        const fullName = `${firstName} ${lastName}`.trim() || form.name || "";
        const company = form.company || "";
        const title = form.title || "";
        
        return template
            .replace(/\{firstName\}/g, firstName || fullName)
            .replace(/\{lastName\}/g, lastName)
            .replace(/\{name\}/g, fullName)
            .replace(/\{company\}/g, company)
            .replace(/\{title\}/g, title);
    };
    
    // Copy and open LinkedIn
    const copyAndOpenLinkedIn = async () => {
        if (!linkedInMessage.trim()) return;
        
        // Apply personalization
        const personalizedMessage = applyTemplate(linkedInMessage);
        
        // Copy to clipboard
        try {
            await navigator.clipboard.writeText(personalizedMessage);
        } catch (err) {
            console.error("Failed to copy:", err);
        }
        
        // Log the interaction
        await addLog("send-linkedin", personalizedMessage);
        
        // Open LinkedIn profile
        openLinkedInProfile();
        
        // Close popup
        setShowLinkedInComposer(false);
        setLinkedInMessage("");
    };
    
    // Filter templates based on search
    const filteredTemplates = useMemo(() => {
        if (!searchTemplate.trim()) return linkedInTemplates;
        const query = searchTemplate.toLowerCase();
        return linkedInTemplates.filter(t => 
            t.name?.toLowerCase().includes(query) ||
            t.description?.toLowerCase().includes(query)
        );
    }, [linkedInTemplates, searchTemplate]);
    
    // Navigate to email composer
    const openEmailComposer = () => {
        if (prospectId) {
            navigate(`/compose-email/${prospectId}`);
        }
    };
    
    // Add/remove tags
    const addTag = (tag: string) => {
        const trimmedTag = tag.trim();
        if (!trimmedTag) return;
        
        const currentTags = Array.isArray(form.tags) ? form.tags : [];
        if (!currentTags.includes(trimmedTag)) {
            const newTags = [...currentTags, trimmedTag];
            set("tags", newTags);
            
            // Notify parent if it's a new tag
            if (onTagCreate && allTags && !allTags.includes(trimmedTag)) {
                onTagCreate(trimmedTag);
            }
        }
        setTagInput("");
        setShowTagSuggestions(false);
    };
    
    const removeTag = (tagToRemove: string) => {
        const currentTags = Array.isArray(form.tags) ? form.tags : [];
        set("tags", currentTags.filter((t: string) => t !== tagToRemove));
    };
    
    // Filtered tag suggestions
    const tagSuggestions = useMemo(() => {
        if (!tagInput.trim() || !allTags) return [];
        const currentTags = Array.isArray(form.tags) ? form.tags : [];
        const query = tagInput.toLowerCase();
        return allTags
            .filter(tag => !currentTags.includes(tag) && tag.toLowerCase().includes(query))
            .slice(0, 5);
    }, [tagInput, allTags, form.tags]);
    
    // Save reminder
    const saveReminder = async () => {
        if (!reminderAction.trim() || !reminderDate) return;
        
        // Combine date and time
        const dateTimeStr = reminderTime 
            ? `${reminderDate}T${reminderTime}:00`
            : `${reminderDate}T09:00:00`;
        
        set("nextAction", reminderAction.trim());
        set("nextActionAt", new Date(dateTimeStr).toISOString());
        
        setShowReminderForm(false);
        setReminderAction("");
        setReminderDate(in3DaysToISOString());
        setReminderTime("14:00");
    };
    
    const clearReminder = () => {
        set("nextAction", "");
        set("nextActionAt", null);
    };
    
    // Social networks helpers
    const addSocialNetwork = () => {
        if (!newSocialPlatform.trim() || !newSocialUrl.trim()) return;
        
        const currentSocials = form.socialNetworks || {};
        const updatedSocials = {
            ...currentSocials,
            [newSocialPlatform.trim()]: newSocialUrl.trim()
        };
        
        set("socialNetworks", updatedSocials);
        setNewSocialPlatform("");
        setNewSocialUrl("");
        setShowAddSocial(false);
    };
    
    const removeSocialNetwork = (platform: string) => {
        const currentSocials = form.socialNetworks || {};
        const { [platform]: _removed, ...remaining } = currentSocials;
        set("socialNetworks", remaining);
    };
    
    // Get icon for social platform
    const getSocialIcon = (platform: string) => {
        const lower = platform.toLowerCase();
        if (lower.includes("instagram")) return "📷";
        if (lower.includes("tiktok")) return "🎵";
        if (lower.includes("twitter") || lower.includes("x")) return "🐦";
        if (lower.includes("facebook")) return "👥";
        if (lower.includes("youtube")) return "▶️";
        if (lower.includes("github")) return "💻";
        return "🔗";
    };
    
    // Delete prospect
    const handleDelete = async () => {
        if (!prospectId) return;
        
        setIsDeleting(true);
        try {
            await deleteProspect(prospectId);
            if (onDelete) {
                onDelete(prospectId);
            }
            setShowDeleteConfirm(false);
            onClose();
        } catch (e) {
            console.error("Failed to delete prospect:", e);
        } finally {
            setIsDeleting(false);
        }
    };
    
    // Save prospect data
    const save = async () => {
        if (!prospectId) return;
        setLoading(true);
        
        const {
            _id: _ignored,
            __v: __vIgnored,
            ownerId: _ownerIdIgnored,
            ...payload
        } = form || {};
        
        // Ensure we include a combined `name` for backends that still expect it
        const payloadToSend: any = { ...payload };
        if (!payloadToSend.name) {
            const fn = (payloadToSend.firstName || "").trim();
            const ln = (payloadToSend.lastName || "").trim();
            if (fn || ln) payloadToSend.name = `${fn} ${ln}`.trim();
        }

        try {
            const res = await updateProspect(prospectId, payloadToSend);
            const updated = {
                ...(form || {}),
                ...(res || {}),
                _id: prospectId,
            };
            setOriginal(updated);
            setForm(updated);
            onSave(updated);
            setLoading(false);
        } catch (e) {
            console.error("Failed to save:", e);
            setLoading(false);
        }
    };

    useEffect(() => {
        const load = async () => {
            if (!prospectId || !isOpen) return;
            setLoading(true);
            const res = await getProspect(prospectId);
            if (res) {
                setOriginal(res);
                setLinkedinStage("discover");
                if (res.linkedinUrl) {
                    getCrmStages()
                        .then((stages) => setLinkedinStage(stages[prospectId]?.stage || "discover"))
                        .catch(() => {});
                }
                let { _id, __v, ...safe } = res || {};

                // Set default notes if empty
                if (!safe.notes) {
                    safe.notes = "Intérêt / Motivation :\n\nObjections / Points de friction :\n\nSignaux humains (intuition, détails personnels) :\n\nIdées / Opportunités :";
                }

                    // If backend only returns a single `name` field, try to split it into firstName/lastName
                    if (!safe.firstName && !safe.lastName && safe.name) {
                        const parts = String(safe.name || "").trim().split(/\s+/).filter(Boolean);
                        if (parts.length === 1) {
                            safe.firstName = parts[0];
                            safe.lastName = "";
                        } else if (parts.length > 1) {
                            safe.firstName = parts.shift() || "";
                            safe.lastName = parts.join(" ");
                        }
                    }

                    setForm(safe);
            }
            setLoading(false);
        };
        load();
    }, [prospectId, isOpen]);

    // (template editor removed)

    const initials = useMemo(() => {
        const f = form || {};
        const n = (f.firstName || f.lastName) ? `${f.firstName || ""} ${f.lastName || ""}`.trim() : (f.name || "");
        if (!n) return "?";
        const parts = n.split(" ").filter(Boolean);
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }, [form]);

    const set = (k: string, v: any) => setForm((p: any) => ({ ...p, [k]: v }));

    // Open on the latest exchange, and follow each new log
    const logsCount = Array.isArray(form?.logs) ? form.logs.length : 0;
    useEffect(() => {
        const el = logsRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [logsCount, isOpen]);

    // Every opening starts on the details tab
    useEffect(() => {
        if (isOpen) setActiveTab("details");
    }, [isOpen, prospectId]);

    // Escape closes the top-most layer; the page behind doesn't scroll while the modal is open
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            // A field that handled Escape itself (e.g. the tag input) keeps it
            if (e.key !== "Escape" || e.defaultPrevented) return;
            if (showDeleteConfirm) { if (!isDeleting) setShowDeleteConfirm(false); }
            else if (showLinkedInComposer) setShowLinkedInComposer(false);
            else onClose();
        };
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [isOpen, showDeleteConfirm, showLinkedInComposer, isDeleting, onClose]);





    return !isOpen ? null : (
        <>
            <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4">
                {/* Backdrop */}
                <div className="absolute inset-0 bg-ink/30" onClick={onClose} />
                {/* Modal panel — sized on the viewport so it never depends on the sidebar's width */}
                <div
                    role="dialog"
                    aria-modal="true"
                    className="relative w-full max-w-[1100px] h-[min(860px,calc(100vh-2rem))] bg-surface rounded-2xl shadow-float overflow-hidden"
                >
                    {/* min-h-0 on the rows/columns lets the inner panes scroll instead of growing the modal */}
                    <div className="grid h-full grid-rows-[minmax(0,1fr)_minmax(0,1fr)] md:grid-rows-1 md:grid-cols-2">
                        {/* Left Panel - Messages */}
                        <div className="min-h-0 border-b md:border-b-0 md:border-r border-border flex flex-col bg-surface">
                            {/* Header with CTAs */}
                            <div className="px-6 py-4 border-b border-border">
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setShowLinkedInComposer(true)}
                                        className="flex-1 flex items-center justify-center gap-2 px-2 py-2.5 bg-[#0A66C2] text-ink-inverse rounded-full hover:bg-[#004182] transition-colors text-xs font-semibold"
                                    >
                                        <FaLinkedinIn size={16} />
                                        Message LinkedIn
                                    </button>
                                    <button
                                        onClick={openEmailComposer}
                                        className="flex-1 flex items-center justify-center gap-2 px-2 py-2.5 bg-ink text-ink-inverse rounded-full hover:bg-ink/80 transition-colors text-xs font-semibold"
                                    >
                                        <Mail size={16} />
                                        Envoyer un email
                                    </button>
                                </div>
                            </div>

                            {/* Messages Area - Show Logs */}
                            <div ref={logsRef} className="flex-1 min-h-0 overflow-y-auto ds-scrollbar p-6 space-y-4">
                                {form.logs && form.logs.length > 0 ? (
                                    form.logs.map((log: any, idx: number) => {
                                        const isOutgoing = log.type?.includes("send") || log.type === "note" || log.type === "message";
                                        const isSent = log.type?.includes("send");
                                        const isReceived = log.type?.includes("received");

                                        return (
                                            <div key={idx} className={cx("flex gap-3", isOutgoing ? "justify-end" : "")}>
                                                <div className="max-w-[80%] min-w-0">
                                                    <div className={cx(
                                                        "rounded-lg p-3 text-sm bg-surface-sunken text-ink-muted break-words",
                                                    )}>
                                                        {/* Type indicator */}
                                                        {(isSent || isReceived) && (
                                                            <div className="flex items-center gap-1.5 mb-1.5 text-xs opacity-70">
                                                                {log.type?.includes("linkedin") && <FaLinkedinIn size={12} />}
                                                                {log.type?.includes("email") && <Mail size={12} />}
                                                                {log.type === "note" && <MessageSquare size={12} />}
                                                                <span className="font-medium">
                                                                    {log.type?.includes("linkedin") && "LinkedIn"}
                                                                    {log.type?.includes("email") && "Email"}
                                                                    {log.type === "note" && "Note"}
                                                                    {isSent && " envoyé"}
                                                                    {isReceived && " reçu"}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {log.subject && (
                                                            <div className="text-ink font-medium mb-1">{log.subject}</div>
                                                        )}
                                                        <div className="text-ink whitespace-pre-wrap">{log.message || "—"}</div>
                                                    </div>
                                                    <div className={cx(
                                                        "text-xs text-ink-faint mt-1",
                                                        isOutgoing ? "text-right" : ""
                                                    )}>
                                                        {log.date && timeUntil(log.date)}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="flex items-center justify-center h-full text-ink-faint text-sm">
                                        Aucun historique de conversation pour le moment. Commencez par envoyer un message !
                                    </div>
                                )}
                            </div>
                            
                            {/* Message Input */}
                            <div className="border-t border-border p-4">
                                <div className="flex gap-2">
                                    <Select
                                        value={selectedLogType}
                                        onChange={(v) => setSelectedLogType(v as any)}
                                        options={[
                                            { value: "message", label: "Note" },
                                            { value: "send-linkedin", label: "LinkedIn Envoyé" },
                                            { value: "received-linkedin", label: "LinkedIn Reçu" },
                                            { value: "send-email", label: "Email Envoyé" },
                                            { value: "received-email", label: "Email Reçu" },
                                        ]}
                                    />
                                    <div className="flex-1 min-w-0 relative flex items-center">
                                        <div className="absolute left-3 pointer-events-none text-ink-faint">
                                            {selectedLogType === "message" && <MessageSquare size={16} />}
                                            {selectedLogType.includes("linkedin") && <FaLinkedinIn size={16} />}
                                            {selectedLogType.includes("email") && <Mail size={16} />}
                                        </div>
                                        <input
                                            type="text"
                                            placeholder={
                                                selectedLogType === "message" ? "Ajouter une note rapide..." :
                                                selectedLogType === "send-linkedin" ? "Logger un message LinkedIn envoyé..." :
                                                selectedLogType === "received-linkedin" ? "Logger un message LinkedIn reçu..." :
                                                selectedLogType === "send-email" ? "Logger un email envoyé..." :
                                                selectedLogType === "received-email" ? "Logger un email reçu..." :
                                                "Tapez votre message..."
                                            }
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSendMessage();
                                                }
                                            }}
                                            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                                        />
                                    </div>
                                    <button 
                                        onClick={handleSendMessage}
                                        disabled={!newMessage.trim()}
                                        aria-label="Ajouter au fil"
                                        className="flex-shrink-0 px-4 py-2 bg-ink rounded-lg hover:bg-ink/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <svg className="w-5 h-5 text-ink-inverse rotate-90" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                        
                        {/* Right Panel - Profile Details */}
                        <div className="min-h-0 flex flex-col bg-surface">
                            {/* Profile Header */}
                            <div className="px-6 py-4 bg-surface border-b border-border">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-4 min-w-0">
                                        <div
                                            className={cx(
                                                "w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center text-lg font-semibold",
                                                "shadow-sm",
                                                avatarColor(form?.firstName || form?.name || ""),
                                            )}
                                        >
                                            {initials}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center mb-2">
                                                <div className="flex min-w-0 overflow-hidden">
                                                    <input
                                                        type="text"
                                                        value={form.firstName || ""}
                                                        onChange={(e) => set("firstName", e.target.value)}
                                                        placeholder="Prénom"
                                                        className="text-lg text-ink border-none focus:outline-none focus:ring-0 bg-transparent p-0 m-0 placeholder:text-ink-faint"
                                                        style={{ width: `${Math.max((form.firstName?.length || 10) + 0.5, 10)}ch` }}
                                                    />
                                                    <input
                                                        type="text"
                                                        value={form.lastName || ""}
                                                        onChange={(e) => set("lastName", e.target.value)}
                                                        placeholder="Nom"
                                                        className="text-lg text-ink border-none focus:outline-none focus:ring-0 bg-transparent p-0 m-0 placeholder:text-ink-faint"
                                                        style={{ width: `${Math.max((form.lastName?.length || 10) + 0.5, 10)}ch` }}
                                                    />
                                                </div>
                                                <a
                                                    href={toUrl(form.linkedinUrl) || "#"}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-[#0072B1] hover:text-blue-700 ml-2"
                                                >
                                                    <FaLinkedinIn size={18} />
                                                </a>
                                                {/* Only once the profile is saved: the LinkedIn page matches on it */}
                                                {prospectId && _original?.linkedinUrl && (
                                                    <span className="ml-2">
                                                        <LinkedinStageMenu
                                                            prospectId={prospectId}
                                                            stage={linkedinStage}
                                                            onChange={(stage, followUpAt) => {
                                                                setLinkedinStage(stage);
                                                                onLinkedinStage?.(prospectId, stage, followUpAt);
                                                            }}
                                                        />
                                                    </span>
                                                )}
                                                {(() => {
                                                    const fullName = [form.firstName, form.lastName].filter(Boolean).join(" ") || form.name || "";
                                                    return fullName ? (
                                                        <a
                                                            href={`/signals/founder?founderName=${encodeURIComponent(fullName)}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="text-ink-faint hover:text-ink-muted ml-2 transition-colors"
                                                            title="Voir les établissements"
                                                        >
                                                            <Building2 size={18} />
                                                        </a>
                                                    ) : null;
                                                })()}
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                <div className="text-sm text-ink-muted mt-1">
                                                    <input
                                                        type="text"
                                                        value={form.title || ""}
                                                        onChange={(e) => set("title", e.target.value)}
                                                        placeholder="Poste"
                                                        className="text-sm text-ink-muted border-none focus:outline-none focus:ring-0 bg-transparent p-0 m-0 w-full placeholder:text-ink-faint"
                                                    />
                                                </div>
                                                <div className="text-sm text-ink-muted flex items-center mt-1">
                                                    <input
                                                        type="text"
                                                        value={form.company || ""}
                                                        onChange={(e) => set("company", e.target.value)}
                                                        placeholder="Entreprise"
                                                        className="text-sm text-ink-muted border-none focus:outline-none focus:ring-0 bg-transparent p-0 m-0 placeholder:text-ink-faint"
                                                        style={{ width: `${Math.max(form.company?.length || 10, 10)}ch` }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <button
                                            className="text-ink-faint hover:text-red-600 transition-colors"
                                            onClick={() => setShowDeleteConfirm(true)}
                                            title="Supprimer"
                                        >
                                            <Trash2 size={20} />
                                        </button>
                                        <button
                                            className="text-ink-faint hover:text-ink-muted"
                                            onClick={onClose}
                                            title="Fermer"
                                        >
                                            <X size={24} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {/* Tabs */}
                            <div className="px-3 py-3">
                                <div className="flex gap-2 bg-surface-sunken rounded-full p-1 shadow-sm">
                                    {[
                                        { key: "details", label: "Détails" },
                                        { key: "reminder", label: "Rappel" },
                                        { key: "notes", label: "Notes" },
                                    ].map((tab) => (
                                        <button
                                            key={tab.key}
                                            className={cx(
                                                "flex-1 py-1 px-4 text-sm font-medium rounded-full transition-all",
                                                activeTab === tab.key
                                                    ? "bg-surface text-ink shadow"
                                                    : "text-ink hover:text-ink/80"
                                            )}
                                            onClick={() => setActiveTab(tab.key as any)}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {/* Tab Content */}
                            <div className="flex-1 min-h-0 overflow-y-auto ds-scrollbar p-6">
                                {activeTab === "reminder" && (
                                    <div>
                                        <h3 className="text-base font-semibold text-ink mb-4">
                                            Rappel programmé
                                        </h3>
                                        
                                        {form.nextAction && form.nextActionAt ? (
                                            <div className="space-y-3">
                                                <div className="flex items-start gap-3 p-4 bg-surface rounded-lg border border-border">
                                                    <div className="mt-0.5">
                                                        <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="text-sm font-medium text-ink">{form.nextAction}</div>
                                                        <div className="text-sm text-ink-muted mt-0.5">
                                                            {new Date(form.nextActionAt).toLocaleString('fr-FR', { 
                                                                dateStyle: 'long', 
                                                                timeStyle: 'short' 
                                                            })}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={clearReminder}
                                                        className="text-ink-faint hover:text-red-600 transition-colors"
                                                        title="Supprimer le rappel"
                                                    >
                                                        <X size={18} />
                                                    </button>
                                                </div>
                                                <button 
                                                    onClick={() => {
                                                        setReminderAction(form.nextAction || "");
                                                        const date = new Date(form.nextActionAt);
                                                        setReminderDate(date.toISOString().split('T')[0]);
                                                        setReminderTime(date.toTimeString().slice(0, 5));
                                                        setShowReminderForm(true);
                                                    }}
                                                    className="w-full py-3 bg-surface text-ink border border-border rounded-lg hover:bg-surface-sunken transition-colors text-sm font-medium"
                                                >
                                                    Modifier le rappel
                                                </button>
                                            </div>
                                        ) : showReminderForm ? (
                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-sm font-medium text-ink-muted mb-1">
                                                        Action à effectuer
                                                    </label>
                                                    <Input
                                                        value={reminderAction}
                                                        onChange={(e: any) => setReminderAction(e.target.value)}
                                                        placeholder="Ex: Relancer par email, Appel de découverte..."
                                                        className="w-full"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-sm font-medium text-ink-muted mb-1">
                                                            Date
                                                        </label>
                                                        <Input
                                                            type="date"
                                                            value={reminderDate}
                                                            onChange={(e: any) => setReminderDate(e.target.value)}
                                                            className="w-full"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-ink-muted mb-1">
                                                            Heure
                                                        </label>
                                                        <Input
                                                            type="time"
                                                            value={reminderTime}
                                                            onChange={(e: any) => setReminderTime(e.target.value)}
                                                            className="w-full"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={saveReminder}
                                                        disabled={!reminderAction.trim() || !reminderDate}
                                                        className="flex-1 py-3 bg-ink text-ink-inverse rounded-lg hover:bg-ink/90 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Enregistrer le rappel
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setShowReminderForm(false);
                                                            setReminderAction("");
                                                            setReminderDate(in3DaysToISOString());
                                                            setReminderTime("14:00");
                                                        }}
                                                        className="px-4 py-3 bg-surface text-ink-muted border border-border rounded-lg hover:bg-surface-sunken transition-colors text-sm font-medium"
                                                    >
                                                        Annuler
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center py-8">
                                                <div className="text-ink-faint mb-4">
                                                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </div>
                                                <p className="text-ink-faint text-sm mb-4">Aucun rappel programmé</p>
                                                <button 
                                                    onClick={() => setShowReminderForm(true)}
                                                    className="w-full py-3 bg-surface text-ink rounded-lg hover:text-ink/80 transition-colors text-md font-medium"
                                                >
                                                    + Ajouter un rappel
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === "details" && (
                                    <div className="space-y-6">

                                        {/* Informations de contact */}
                                        <div>
                                            <h3 className="text-md text-ink mb-3">Informations de contact</h3>
                                            <div className="space-y-3">
                                                <div className="flex items-center text-sm">
                                                    <svg className="w-5 h-5 text-ink-faint flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                                                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                                                    </svg>
                                                    <input 
                                                        value={form.email || ""} 
                                                        onChange={(e: any) => set("email", e.target.value?.trim())} 
                                                        placeholder="sophie.martin@techcorp.com"
                                                        className="flex-1 border-0 focus:border-b border-transparent focus:border-black bg-transparent px-2 py-1 text-sm text-ink placeholder-gray-400 focus:outline-none transition-colors"
                                                    />
                                                </div>
                                                <div className="flex items-center text-sm">
                                                    <svg className="w-5 h-5 text-ink-faint flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                                                    </svg>
                                                    <input 
                                                        value={form.phone || ""} 
                                                        onChange={(e: any) => set("phone", e.target.value)} 
                                                        placeholder="+33 6 12 34 56 78"
                                                        className="flex-1 border-0 focus:border-b border-transparent focus:border-black bg-transparent px-2 py-1 text-sm text-ink placeholder-gray-400 focus:outline-none transition-colors"
                                                    />
                                                </div>
                                                <div className="flex items-center text-sm">
                                                    <FaLinkedinIn className="w-5 h-5 text-ink-faint flex-shrink-0" />
                                                    <input 
                                                        value={form.linkedinUrl || ""} 
                                                        onChange={(e: any) => set("linkedinUrl", e.target.value)} 
                                                        placeholder="linkedin.com/in/sophiemartin"
                                                        className="flex-1 border-0 focus:border-b border-transparent focus:border-black bg-transparent px-2 py-1 text-sm text-ink placeholder-gray-400 focus:outline-none transition-colors"
                                                    />
                                                </div>
                                                <div className="flex items-center text-sm">
                                                    <svg className="w-5 h-5 text-ink-faint flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd"/>
                                                    </svg>
                                                    <input 
                                                        value={form.website || ""} 
                                                        onChange={(e: any) => set("website", e.target.value)} 
                                                        placeholder="techcorp.com"
                                                        className="flex-1 border-0 focus:border-b border-transparent focus:border-black bg-transparent px-2 py-1 text-sm text-ink placeholder-gray-400 focus:outline-none transition-colors"
                                                    />
                                                </div>
                                                
                                                {/* Establishment link */}
                                                {form.establishmentId && (
                                                    <div className="flex items-center text-sm">
                                                        <svg className="w-5 h-5 text-ink-faint flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd"/>
                                                        </svg>
                                                        <a
                                                            href={`/establishments/${form.establishmentId}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="flex-1 px-2 py-1 text-sm text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                                                        >
                                                            Voir l'établissement
                                                            <ExternalLink size={12} />
                                                        </a>
                                                    </div>
                                                )}

                                                {/* Social Networks */}
                                                {form.socialNetworks && Object.keys(form.socialNetworks).length > 0 && (
                                                    <div className="pt-2 space-y-2">
                                                        {Object.entries(form.socialNetworks).map(([platform, url]: [string, any]) => (
                                                            <div key={platform} className="flex items-center text-sm group">
                                                                <span className="w-5 h-5 flex items-center justify-center text-ink-faint flex-shrink-0">
                                                                    {getSocialIcon(platform)}
                                                                </span>
                                                                <a
                                                                    href={toUrl(url)}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className="flex-1 px-2 py-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                                                >
                                                                    {platform}
                                                                </a>
                                                                <button
                                                                    onClick={() => removeSocialNetwork(platform)}
                                                                    className="opacity-0 group-hover:opacity-100 text-ink-faint hover:text-red-600 transition-all"
                                                                    title="Supprimer"
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                
                                                {/* Add Social Network */}
                                                {showAddSocial ? (
                                                    <div className="pt-2 space-y-2 border-t border-border mt-2">
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                value={newSocialPlatform}
                                                                onChange={(e) => setNewSocialPlatform(e.target.value)}
                                                                placeholder="Plateforme (ex: Instagram)"
                                                                className="flex-1 px-3 py-1.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                                                            />
                                                            <input
                                                                type="text"
                                                                value={newSocialUrl}
                                                                onChange={(e) => setNewSocialUrl(e.target.value)}
                                                                placeholder="URL"
                                                                className="flex-1 px-3 py-1.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                                                            />
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={addSocialNetwork}
                                                                disabled={!newSocialPlatform.trim() || !newSocialUrl.trim()}
                                                                className="px-3 py-1.5 bg-ink text-ink-inverse rounded-lg text-xs hover:bg-ink/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                Ajouter
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setShowAddSocial(false);
                                                                    setNewSocialPlatform("");
                                                                    setNewSocialUrl("");
                                                                }}
                                                                className="px-3 py-1.5 border border-border rounded-lg text-xs text-ink-muted hover:bg-surface-sunken transition-colors"
                                                            >
                                                                Annuler
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => setShowAddSocial(true)}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-ink-muted hover:text-ink transition-colors"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                        </svg>
                                                        Ajouter un réseau social
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Tags Section */}
                                        <div>
                                            <h3 className="text-md text-ink mb-3">Tags</h3>
                                            <div className="flex flex-wrap gap-2 items-center">
                                                {/* Existing tags */}
                                                {Array.isArray(form.tags) && form.tags.map((tag: string, idx: number) => (
                                                    <div
                                                        key={idx}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-xs"
                                                    >
                                                        <span>{tag}</span>
                                                        <button
                                                            onClick={() => removeTag(tag)}
                                                            className="hover:text-blue-900 transition-colors"
                                                            title="Supprimer"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                ))}
                                                {/* Add tag button/input */}
                                                <div className="relative tag-input-container">
                                                    {showTagSuggestions ? (
                                                        <div className="relative">
                                                            <input
                                                                autoFocus
                                                                type="text"
                                                                value={tagInput}
                                                                onChange={(e: any) => setTagInput(e.target.value)}
                                                                onKeyDown={(e: any) => {
                                                                    if (e.key === "Enter" && tagInput.trim()) {
                                                                        e.preventDefault();
                                                                        addTag(tagInput);
                                                                    } else if (e.key === "Escape") {
                                                                        e.preventDefault();
                                                                        setShowTagSuggestions(false);
                                                                        setTagInput("");
                                                                    }
                                                                }}
                                                                onBlur={(e: any) => {
                                                                    // Small delay to allow clicking on suggestions
                                                                    setTimeout(() => {
                                                                        if (tagInput.trim()) {
                                                                            addTag(tagInput);
                                                                        } else {
                                                                            setShowTagSuggestions(false);
                                                                            setTagInput("");
                                                                        }
                                                                    }, 150);
                                                                }}
                                                                placeholder="Nouveau tag..."
                                                                className="px-3 py-1.5 border border-black rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-accent min-w-[150px]"
                                                            />
                                                            
                                                            {/* Tag suggestions dropdown */}
                                                            {tagSuggestions.length > 0 && tagInput.trim() && (
                                                                <div className="absolute z-10 left-0 top-full mt-1 bg-surface border border-border rounded-2xl shadow-float max-h-48 overflow-y-auto overflow-x-hidden ds-scrollbar min-w-[200px]">
                                                                    {tagSuggestions.map((tag: string, idx: number) => (
                                                                        <button
                                                                            key={idx}
                                                                            onMouseDown={(e) => {
                                                                                e.preventDefault();
                                                                                addTag(tag);
                                                                            }}
                                                                            className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors text-sm"
                                                                        >
                                                                            {tag}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => setShowTagSuggestions(true)}
                                                            className="inline-flex items-center gap-1 px-3 py-1.5 border-2 border-dashed border-border-strong text-ink-faint rounded-full text-xs hover:border-blue-400 hover:text-blue-600 transition-colors"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                            </svg>
                                                            <span>Ajouter</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === "notes" && (
                                    <div>
                                        <Textarea 
                                            rows={12} 
                                            value={form.notes || ""} 
                                            onChange={(e: any) => set("notes", e.target.value)}
                                            placeholder="Ajoutez vos notes ici..."
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Save Button */}
                            <div className="px-6 py-4 bg-surface border-t border-border">
                                <button
                                    onClick={save}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-ink text-ink-inverse rounded-full hover:bg-ink/90 transition-colors text-xs font-medium"
                                >
                                    <Save size={16} />
                                    Sauvegarder
                                </button>
                            </div>
                        </div>
                    </div>


                </div>
            </div>
            
            {/* LinkedIn Message Composer Popup */}
            {showLinkedInComposer && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
                    <div 
                        className="absolute inset-0 bg-ink/50" 
                        onClick={() => setShowLinkedInComposer(false)}
                    />
                    <div className="relative bg-surface rounded-2xl shadow-float w-full max-w-3xl max-h-[90vh] flex flex-col">
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-ink">Message LinkedIn</h3>
                                <p className="text-sm text-ink mt-0.5">
                                    Choisissez un modèle, personnalisez-le, copiez et ouvrez le profil LinkedIn
                                </p>
                            </div>
                            <button
                                onClick={() => setShowLinkedInComposer(false)}
                                className="text-ink-faint hover:text-ink-muted transition-colors"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-h-0 overflow-y-auto ds-scrollbar flex flex-col p-6 space-y-4">
                            {/* Search Templates */}
                            <div>
                                <input
                                    type="text"
                                    value={searchTemplate}
                                    onChange={(e) => setSearchTemplate(e.target.value)}
                                    placeholder="Rechercher des modèles..."
                                    className="w-full px-4 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                                />
                            </div>
                            
                            {/* Templates List */}
                            <div className="flex-shrink-0">
                                <h4 className="text-sm font-medium text-ink-muted mb-2">Modèles</h4>
                                <div className="max-h-48 overflow-y-auto ds-scrollbar border border-border rounded-lg">
                                    {loadingTemplates ? (
                                        <div className="p-4 text-center text-ink text-sm">
                                            Chargement des modèles...
                                        </div>
                                    ) : filteredTemplates.length === 0 ? (
                                        <div className="p-4 text-center text-ink text-sm">
                                            Aucun modèle trouvé
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-100">
                                            {filteredTemplates.map((template) => (
                                                <button
                                                    key={template._id}
                                                    onClick={() => {
                                                        const content = template.text || template.html || "";
                                                        setLinkedInMessage(content);
                                                    }}
                                                    className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors"
                                                >
                                                    <div className="font-medium text-sm text-ink">
                                                        {template.name || "Sans titre"}
                                                    </div>
                                                    {template.description && (
                                                        <div className="text-xs text-ink mt-0.5">
                                                            {template.description}
                                                        </div>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Message Editor */}
                            <div className="flex-1 flex flex-col">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-medium text-ink-muted">Votre message</h4>
                                    <div className="flex gap-2 text-xs text-ink">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const pos = messageRef.current?.selectionStart || 0;
                                                const newMsg = linkedInMessage.slice(0, pos) + "{firstName}" + linkedInMessage.slice(pos);
                                                setLinkedInMessage(newMsg);
                                            }}
                                            className="text-blue-600 hover:underline"
                                        >
                                            {"{firstName}"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const pos = messageRef.current?.selectionStart || 0;
                                                const newMsg = linkedInMessage.slice(0, pos) + "{company}" + linkedInMessage.slice(pos);
                                                setLinkedInMessage(newMsg);
                                            }}
                                            className="text-blue-600 hover:underline"
                                        >
                                            {"{company}"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const pos = messageRef.current?.selectionStart || 0;
                                                const newMsg = linkedInMessage.slice(0, pos) + "{title}" + linkedInMessage.slice(pos);
                                                setLinkedInMessage(newMsg);
                                            }}
                                            className="text-blue-600 hover:underline"
                                        >
                                            {"{title}"}
                                        </button>
                                    </div>
                                </div>
                                <textarea
                                    ref={messageRef}
                                    value={linkedInMessage}
                                    onChange={(e) => setLinkedInMessage(e.target.value)}
                                    placeholder="Écrivez votre message LinkedIn ici..."
                                    className="flex-1 min-h-[200px] w-full px-4 py-3 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                                />
                            </div>
                        </div>
                        
                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-border flex items-center justify-between">
                            <button
                                onClick={() => {
                                    setShowLinkedInComposer(false);
                                    setLinkedInMessage("");
                                }}
                                className="px-4 py-2 text-sm text-ink-muted hover:text-ink transition-colors"
                            >
                                Annuler
                            </button>
                            <div className="flex gap-2">
                                <button
                                    onClick={async () => {
                                        const personalizedMessage = applyTemplate(linkedInMessage);
                                        try {
                                            await navigator.clipboard.writeText(personalizedMessage);
                                            // You could add a toast notification here
                                        } catch (err) {
                                            console.error("Failed to copy:", err);
                                        }
                                    }}
                                    disabled={!linkedInMessage.trim()}
                                    className="flex items-center gap-2 px-4 py-2 border border-border-strong rounded-lg text-sm font-medium text-ink-muted hover:bg-surface-sunken transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Copy size={16} />
                                    Copier
                                </button>
                                <button
                                    onClick={copyAndOpenLinkedIn}
                                    disabled={!linkedInMessage.trim()}
                                    className="flex items-center gap-2 px-4 py-2 bg-[#0A66C2] text-ink-inverse rounded-lg text-sm font-medium hover:bg-[#004182] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ExternalLink size={16} />
                                    Copier, Logger & Ouvrir LinkedIn
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Delete Confirmation Popup */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-[2100] flex items-center justify-center p-4">
                    <div 
                        className="absolute inset-0 bg-ink/25" 
                        onClick={() => !isDeleting && setShowDeleteConfirm(false)}
                    />
                    <div className="relative bg-surface rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] w-full max-w-md animate-in fade-in zoom-in duration-200">
                        {/* Header */}
                        <div className="px-6 py-5 border-b border-border">
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                    <div>
                                        <h3 className="text-lg rounded font-semibold text-ink">Supprimer le prospect</h3>
                                        <p className="text-sm text-ink-muted mt-0.5">Cette action est définitive</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => !isDeleting && setShowDeleteConfirm(false)}
                                    disabled={isDeleting}
                                    className="text-ink-faint hover:text-ink-muted transition-colors disabled:opacity-50"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>
                        
                        {/* Content */}
                        <div className="px-6 py-5">
                            <p className="text-ink-muted text-sm leading-relaxed">
                                Êtes-vous sûr de vouloir supprimer{" "}
                                <span className="font-semibold text-ink">
                                    {form.firstName || form.lastName 
                                        ? `${form.firstName || ""} ${form.lastName || ""}`.trim()
                                        : form.name || "ce prospect"}
                                </span>
                                {form.company && (
                                    <>
                                        {" "}de{" "}
                                        <span className="font-semibold text-ink">{form.company}</span>
                                    </>
                                )}
                                {" "}?
                            </p>
                            
                            {form.logs && form.logs.length > 0 && (
                                <div className="mt-4 p-3 bg-surface-sunken rounded-lg border border-border">
                                    <div className="flex items-start gap-2.5">
                                        <svg className="w-4 h-4 text-ink-faint flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <div className="text-xs text-ink-muted">
                                            <p><span className="font-medium text-ink">{form.logs.length}</span> conversation{form.logs.length > 1 ? "s" : ""} et toutes les données associées seront supprimées</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Footer */}
                        <div className="px-6 py-4 bg-surface-sunken border-t border-border flex gap-3">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={isDeleting}
                                className="flex-1 px-4 py-2.5 bg-surface border border-border rounded-full text-sm font-medium text-ink-muted hover:bg-surface-sunken hover:border-border-strong transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="flex-1 px-4 py-2.5 bg-red-600 rounded-full text-ink-inverse text-sm font-medium hover:bg-red-700 active:bg-red-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
                            >
                                {isDeleting ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Suppression...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 size={16} />
                                        Supprimer
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
